import { useEffect, useState, type FormEvent } from "react";
import { Modal } from "@veorest/ui";
import { Check, Loader2, RefreshCw, X } from "lucide-react";
import type { PartnerDriverMode } from "../../lib/partnerDrivers";
import { DriverModeField } from "./DriverModeField";
import { useI18n } from "../../i18n";
import { usePartnerSessionStore } from "../../lib/partnerAuth";
import {
  checkAccountUsername,
  generatePassword,
  handleErrorKey,
  type HandleCheck,
} from "../../lib/partnerAccounts";

export type DriverCreateMode = "passcode" | "ready";

export interface DriverCreateValues {
  mode: DriverCreateMode;
  name: string;
  phone: string;
  address: string;
  vehicleType: "bicycle" | "motorcycle" | "car";
  vehicleNumber: string;
  vehicleModel: string;
  vehicleColor: string;
  driverMode: PartnerDriverMode;
  /** Ready-to-Use only */
  username: string;
  /** Ready-to-Use only */
  password: string;
}

interface DriverCreateModalProps {
  open: boolean;
  busy?: boolean;
  defaultMode?: PartnerDriverMode;
  onClose: () => void;
  onSubmit: (values: DriverCreateValues) => Promise<void>;
}

const MIN_PASSWORD = 8;
const CHECK_DEBOUNCE_MS = 450;

export function DriverCreateModal({
  open,
  busy,
  defaultMode = "paid",
  onClose,
  onSubmit,
}: DriverCreateModalProps) {
  const { t } = useI18n();
  const partnerId = usePartnerSessionStore((s) => s.session?.partnerId ?? null);
  const [mode, setMode] = useState<DriverCreateMode>("passcode");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [vehicleType, setVehicleType] = useState<"bicycle" | "motorcycle" | "car">(
    "motorcycle"
  );
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [vehicleModel, setVehicleModel] = useState("");
  const [vehicleColor, setVehicleColor] = useState("");
  const [driverMode, setDriverMode] = useState<PartnerDriverMode>(defaultMode);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [formError, setFormError] = useState("");
  const [usernameCheck, setUsernameCheck] = useState<HandleCheck | null>(null);
  const [usernameChecking, setUsernameChecking] = useState(false);

  useEffect(() => {
    if (open) {
      setDriverMode(defaultMode);
      setMode("passcode");
    }
  }, [open, defaultMode]);

  useEffect(() => {
    if (mode !== "ready" || !partnerId) {
      setUsernameCheck(null);
      return;
    }
    const value = username.trim().toLowerCase();
    if (value.length < 3) {
      setUsernameCheck(null);
      return;
    }
    setUsernameChecking(true);
    const timer = window.setTimeout(() => {
      checkAccountUsername(partnerId, value, "driver")
        .then((res) => setUsernameCheck(res))
        .catch(() => setUsernameCheck(null))
        .finally(() => setUsernameChecking(false));
    }, CHECK_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [mode, partnerId, username]);

  const renderHandleStatus = () => {
    if (usernameChecking) {
      return (
        <span className="inline-flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t("accounts.checking")}
        </span>
      );
    }
    if (!usernameCheck) return null;
    if (usernameCheck.available) {
      return (
        <span className="inline-flex items-center gap-1 text-[var(--accent)]">
          <Check className="h-3 w-3" />
          {t("accounts.available")}
        </span>
      );
    }
    return (
      <span className="text-[var(--danger)]">
        {t(handleErrorKey(usernameCheck.error))}
      </span>
    );
  };

  if (!open) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (mode === "ready") {
      if (password.length < MIN_PASSWORD) {
        setFormError(t("accounts.passwordMin"));
        return;
      }
      if (usernameCheck && !usernameCheck.available) {
        setFormError(t(handleErrorKey(usernameCheck.error)));
        return;
      }
    }
    setFormError("");

    await onSubmit({
      mode,
      name,
      phone,
      address,
      vehicleType,
      vehicleNumber,
      vehicleModel,
      vehicleColor,
      driverMode,
      username: username.trim().toLowerCase(),
      password: mode === "ready" ? password : "",
    });

    setMode("passcode");
    setName("");
    setPhone("");
    setAddress("");
    setVehicleType("motorcycle");
    setVehicleNumber("");
    setVehicleModel("");
    setVehicleColor("");
    setUsername("");
    setPassword("");
    setUsernameCheck(null);
    setDriverMode(defaultMode);
  };


  return (
    <Modal open={open} onClose={onClose} panelClassName="max-w-md">
      <div className="flex items-center justify-between border-b border-[var(--border-subtle)] px-5 py-4">
        <h2 id="create-driver-title" className="text-lg font-extrabold text-[var(--text-primary)]">
            {t("drivers.createTitle")}
          </h2>
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--border-subtle)]"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4 px-5 py-4">
          <div
            className="grid grid-cols-2 gap-1 rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] p-1"
            role="radiogroup"
            aria-label={t("drivers.createTitle")}
          >
            {(
              [
                { key: "passcode" as const, label: t("drivers.modePasscode") },
                { key: "ready" as const, label: t("drivers.modeReady") },
              ]
            ).map((option) => {
              const active = mode === option.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => {
                    setMode(option.key);
                    setFormError("");
                  }}
                  className={`min-h-[42px] rounded-lg px-3 text-xs font-black transition ${
                    active
                      ? "bg-[var(--surface)] text-[var(--accent)] shadow-sm"
                      : "text-[var(--text-secondary)]"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] leading-5 text-[var(--text-muted)]">
            {mode === "ready" ? t("drivers.readyHint") : t("drivers.passcodeHint")}
          </p>

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
              {t("drivers.name")}
            </span>
            <input
              className="input-base w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
              autoFocus
              placeholder={t("drivers.namePlaceholder")}
            />
          </label>

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
              {t("drivers.phone")}
            </span>
            <input
              className="input-base w-full"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              placeholder="+964…"
              inputMode="tel"
            />
          </label>

          {mode === "ready" ? (
            <label className="block space-y-1.5">
              <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
                {t("drivers.address")}
              </span>
              <input
                className="input-base w-full"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={t("clients.addressPlaceholder")}
              />
            </label>
          ) : null}

          <DriverModeField value={driverMode} onChange={setDriverMode} />

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
              {t("drivers.vehicleType")}
            </span>
            <select
              className="select-base w-full"
              value={vehicleType}
              onChange={(e) =>
                setVehicleType(e.target.value as "bicycle" | "motorcycle" | "car")
              }
            >
              <option value="motorcycle">{t("drivers.motorcycle")}</option>
              <option value="car">{t("drivers.car")}</option>
              <option value="bicycle">{t("drivers.bicycle")}</option>
            </select>
          </label>

          {mode === "ready" ? (
            <div className="form-grid app-modal-grid-2">
              <label className="block space-y-1.5">
                <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
                  {t("drivers.vehicleModel")}
                </span>
                <input
                  className="input-base w-full"
                  value={vehicleModel}
                  onChange={(e) => setVehicleModel(e.target.value)}
                  placeholder={t("common.dash")}
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
                  {t("drivers.vehicleColor")}
                </span>
                <input
                  className="input-base w-full"
                  value={vehicleColor}
                  onChange={(e) => setVehicleColor(e.target.value)}
                  placeholder={t("common.dash")}
                />
              </label>
            </div>
          ) : null}

          <label className="block space-y-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
              {t("drivers.vehicleNumber")}
            </span>
            <input
              className="input-base w-full"
              value={vehicleNumber}
              onChange={(e) => setVehicleNumber(e.target.value)}
              placeholder={t("common.dash")}
            />
          </label>

          {mode === "ready" ? (
            <>
              <label className="block space-y-1.5">
                <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
                  {t("drivers.username")}
                </span>
                <input
                  className="input-base w-full"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  minLength={3}
                  maxLength={30}
                  autoCapitalize="none"
                  spellCheck={false}
                  dir="ltr"
                  placeholder="driver-name"
                />
                <span className="flex flex-wrap items-center gap-1.5 text-[11px] text-[var(--text-muted)]">
                  {t("drivers.usernameHint")}
                  {renderHandleStatus()}
                </span>
              </label>

              <label className="block space-y-1.5">
                <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
                  {t("accounts.initialPassword")}
                </span>
                <div className="flex items-center gap-2">
                  <input
                    className="input-base w-full"
                    type="text"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setFormError("");
                    }}
                    required
                    minLength={MIN_PASSWORD}
                    autoComplete="new-password"
                    spellCheck={false}
                    dir="ltr"
                    placeholder={t("accounts.passwordPlaceholder")}
                  />
                  <button
                    type="button"
                    onClick={() => setPassword(generatePassword())}
                    className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-xl border border-[var(--border-subtle)] px-3 text-xs font-bold text-[var(--accent)]"
                    title={t("accounts.generate")}
                  >
                    <RefreshCw className="h-4 w-4" />
                    {t("accounts.generate")}
                  </button>
                </div>
                <span className="text-[11px] text-[var(--text-muted)]">
                  {t("accounts.passwordMin")}
                </span>
              </label>
            </>
          ) : null}

          {formError ? (
            <p className="text-xs font-bold text-[var(--danger)]">{formError}</p>
          ) : null}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              className="btn-secondary flex-1"
              onClick={onClose}
              disabled={busy}
            >
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn-primary flex-1" disabled={busy}>
              {busy
                ? t("drivers.creating")
                : mode === "ready"
                  ? t("drivers.createReady")
                  : t("drivers.create")}
            </button>
          </div>
        </form>
    </Modal>
  );
}
